import set from 'lodash/set';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';

const EVENT_NAME = 'local-storage';
const CHANNEL_NAME = 'local-storage';

function getChannel() {
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      return new BroadcastChannel(CHANNEL_NAME);
    }
  } catch {
    // Ignore
  }

  return null;
}

function safeParse(json, fallback) {
  try { return json ? JSON.parse(json) : fallback; } catch { return fallback; }
}

export const storageBus = {
  /**
   * Read a JSON value, optionally from a namespaced object.
   */
  read({ key, namespace = null, storage = 'localStorage', initialValue = undefined }) {
    try {
      if (namespace) {
        const raw = window[storage].getItem(namespace);
        const data = safeParse(raw, {});
        return get(data, key, initialValue);
      }
      const item = window[storage].getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (e) {
      console.error(`Error reading ${storage} key "${key}":`, e);
      return initialValue;
    }
  },

  /**
   * Write a JSON value and notify listeners if it actually changed.
   */
  write({ key, value, namespace = null, storage = 'localStorage' }) {
    try {
      // Read previous value for change detection.
      let prevValue;
      if (namespace) {
        const raw = window[storage].getItem(namespace);
        const data = safeParse(raw, {});
        prevValue = get(data, key);
        // Persist the write regardless of equality.
        const updated = set({ ...data }, key, value);
        window[storage].setItem(namespace, JSON.stringify(updated));
      } else {
        const raw = window[storage].getItem(key);
        prevValue = safeParse(raw, undefined);
        // Persist the write regardless of equality.
        window[storage].setItem(key, JSON.stringify(value));
      }

      // Only broadcast if value actually changed (deep compare).
      if (!isEqual(prevValue, value)) {
        this.notify({ key, namespace, storage });
      }
    } catch (e) {
      console.error(`Error setting ${storage} key "${key}":`, e);
      throw e;
    }
  },

  /**
   * Manually notify (same-tab + BroadcastChannel).
   */
  notify({ key, namespace = null, storage = 'localStorage' }) {
    try {
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { storage, namespace, key } }));
    } catch (e) {
      console.error('Error dispatching local storage event:', e);
    }

    try {
      const channel = getChannel();
      channel?.postMessage({ storage, namespace, key });
      // Best-effort close to avoid leaks in ephemeral calls
      try { channel?.close(); } catch {
        // Ignore
      }
    } catch (e) {
      console.error('Error posting BroadcastChannel message:', e);
    }
  },

  /**
   * Subscribe to changes for a specific key/namespace/storage.
   * Returns an unsubscribe function.
   *
   * onChange receives ({ key, namespace, storage, read }) and you can call read()
   * to get the fresh value at the time of the event.
   */
  subscribe({ key, namespace = null, storage = 'localStorage' }, onChange) {
    const channel = getChannel();

    const refresh = () => {
      // Reader bound to current params.
      const read = () => this.read({ key, namespace, storage });
      onChange?.({ key, namespace, storage, read });
    };

    const onStorage = (e) => {
      if (e.storageArea !== window[storage]) return;
      if (namespace ? e.key === namespace : e.key === key) refresh();
    };

    const onLocal = (e) => {
      const d = e.detail || {};
      if (d.storage === storage && d.namespace === namespace && d.key === key) refresh();
    };

    const onChannel = (msg) => {
      const d = msg?.data || {};
      if (d.storage === storage && d.namespace === namespace && d.key === key) refresh();
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener(EVENT_NAME, onLocal);
    if (channel) channel.addEventListener('message', onChannel);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(EVENT_NAME, onLocal);
      if (channel) channel.removeEventListener('message', onChannel);
      try { channel?.close(); } catch {
        // Ignore
      }
    };
  },
};
